import { Head, Link, usePage } from "@inertiajs/react"

import AppLogo from "@/components/app-logo"
import AppLogoIcon from "@/components/app-logo-icon"
import SkullWithEyes from "@/components/skull-with-eyes"
import { Button } from "@/components/ui/button"
import { listsPath, newRegistrationPath, newSessionPath } from "@/routes"
import type { SharedData } from "@/types"

export default function Welcome() {
  const page = usePage<SharedData>()
  const { auth, app_url: appUrl } = page.props

  const canonicalUrl = new URL("/", appUrl).toString()
  const ogImageUrl = new URL("/og-home.png", appUrl).toString()
  const twitterImageUrl = new URL("/og-home-twitter.png", appUrl).toString()

  return (
    <>
      <Head title="Every task gets done or gets it.">
        <meta
          name="description"
          content="The only to-do app that cleans itself up. Stop letting your backlog haunt you. If it matters, you'll do it. If you don't? We murder it for you."
        />
        <meta
          property="og:title"
          content="ToDoOrDie — Every task gets done or gets it."
        />
        <meta
          property="og:description"
          content="The only to-do app that cleans itself up. Stop letting your backlog haunt you. If it matters, you'll do it. If you don't? We murder it for you."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta
          name="twitter:title"
          content="ToDoOrDie — Every task gets done or gets it."
        />
        <meta
          name="twitter:description"
          content="The only to-do app that cleans itself up. Stop letting your backlog haunt you."
        />
        <meta name="twitter:image" content={twitterImageUrl} />
      </Head>

      <div className="vignette noise bg-bg-body text-bone min-h-screen">
        {/* Navigation */}
        <nav className="from-bg-body fixed inset-x-0 top-0 z-50 bg-gradient-to-b to-transparent px-6 py-4">
          <div className="mx-auto flex max-w-4xl items-center justify-between">
            <Link href="/" className="flex items-center">
              <AppLogo />
            </Link>

            <div className="flex items-center gap-6">
              {auth?.user && (
                <>
                  <Button asChild>
                    <Link href={listsPath()}>Go to Dashboard</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="flex min-h-screen items-center px-6 pt-24 pb-12 lg:pt-32">
          <div className="mx-auto grid w-full max-w-4xl items-center gap-8 lg:grid-cols-2">
            <div className="animate-[fadeInUp_0.8s_ease-out] text-center lg:max-w-xl lg:text-left">
              <h1 className="font-display mb-6 bg-gradient-to-b from-white to-zinc-400 bg-clip-text text-5xl leading-[0.95] tracking-wide text-transparent md:text-6xl lg:text-7xl">
                EVERY TASK
                <br />
                GETS DONE
                <br />
                OR{" "}
                <span className="text-red bg-none [text-shadow:0_0_30px_var(--red-glow)]">
                  GETS IT.
                </span>
              </h1>
              <p className="text-bone-muted mx-auto mb-8 max-w-md text-lg lg:mx-0">
                The only to-do app that cleans itself up. Stop letting your
                backlog haunt you. If it matters, you&apos;ll do it. If you
                don&apos;t? <strong>We murder it for you.</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-4 lg:justify-start">
                {auth?.user ? (
                  <Button asChild size="lg">
                    <Link href={listsPath()}>Go to Dashboard</Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild size="lg">
                      <Link href={newRegistrationPath()}>
                        Start Killing Tasks (Free)
                      </Link>
                    </Button>
                    <Button variant="outline" asChild size="lg">
                      <Link href={newSessionPath()}>Sign In</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="flex animate-[fadeIn_1s_ease-out_0.3s_both] items-center justify-center">
              <SkullWithEyes className="h-80 w-80 lg:h-96 lg:w-96" />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          id="how-it-works"
          className="border-border bg-bg-card border-y px-6 py-20"
        >
          <div className="mx-auto max-w-4xl">
            <div className="mb-12 text-center">
              <h2 className="font-display mb-3 text-4xl tracking-wide md:text-5xl">
                HOW IT WORKS
              </h2>
              <p className="text-bone-muted mx-auto max-w-md">
                Three simple steps. No complicated systems. No eternal to-do
                lists.
              </p>
            </div>

            <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
              {[
                {
                  num: "01",
                  title: "LIE TO YOURSELF",
                  desc: 'Write down a task. Give it a due date. Pretend that this is the time you actually "organize the spice rack."',
                },
                {
                  num: "02",
                  title: "PANIC SLIGHTLY",
                  desc: "The countdown is on. You have 7 days (or whatever you set). That tick-tock sound? That's motivation.",
                },
                {
                  num: "03",
                  title: "LET IT GO",
                  desc: "Miss the deadline? Poof. It's gone. Sent to the void. No guilt, just a digital tombstone and a cleaner list.",
                },
              ].map((step, i) => (
                <div
                  key={i}
                  className="animate-[fadeInUp_0.6s_ease-out_both] p-6 text-center"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="font-display text-red mb-3 text-6xl opacity-80 [text-shadow:0_2px_10px_var(--red-glow)]">
                    {step.num}
                  </div>
                  <h3 className="font-display mb-2 text-xl tracking-wider">
                    {step.title}
                  </h3>
                  <p className="text-bone-muted text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="px-6 py-20">
          <div className="mx-auto grid max-w-4xl items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-display mb-6 text-3xl tracking-wide md:text-4xl">
                YOUR TO-DO LIST IS A GUILT MUSEUM.
              </h2>
              <p className="text-bone-muted mb-4">
                &quot;Paint the guest room.&quot; &quot;Learn Italian.&quot;
                &quot;Call that one aunt.&quot;
              </p>
              <p className="text-bone-muted mb-4">
                They&apos;ve been staring at you since 2019. Most apps hoard
                these failures like trophies. They want you to feel bad.
              </p>
              <p className="text-bone mb-4 font-semibold">
                We believe in euthanasia for busywork.
              </p>
              <p className="text-bone-muted">
                If you haven&apos;t done it by now, you aren&apos;t going to.
                Let us put that task out of its misery so you can move on with
                your life.
              </p>
            </div>

            <div className="flex justify-center">
              <div className="tombstone-shape border-border from-bg-card to-bg-body relative max-w-[280px] border bg-gradient-to-b p-8 text-center shadow-lg">
                <div className="absolute -bottom-5 left-1/2 h-5 w-[120%] -translate-x-1/2 rounded-[50%] bg-gradient-to-b from-black/50 to-transparent" />
                <div className="font-display text-bone-dim mb-3 text-sm tracking-[0.3em]">
                  R.I.P.
                </div>
                <div className="font-display decoration-red mb-2 text-xl line-through decoration-2 opacity-70">
                  &quot;Learn Spanish&quot;
                </div>
                <div className="text-bone-dim text-sm">2021 — 2021</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-24 text-center lg:py-32">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[600px] w-[600px] bg-[radial-gradient(circle,var(--red-glow)_0%,transparent_70%)] opacity-15" />
          </div>
          <div className="relative mx-auto max-w-4xl">
            <h2 className="font-display mb-4 bg-gradient-to-b from-white to-zinc-400 bg-clip-text text-4xl tracking-wide text-transparent md:text-5xl lg:text-6xl">
              BURY THE BUSYWORK.
            </h2>
            <p className="text-bone-muted mx-auto mb-8 max-w-md text-lg">
              Join thousands of recovering hoarders who finally have an empty
              list.
            </p>
            {auth?.user ? (
              <Button asChild size="lg">
                <Link href={listsPath()}>Go to Dashboard</Link>
              </Button>
            ) : (
              <Button asChild size="lg">
                <Link href={newRegistrationPath()}>Get ToDoOrDie Now</Link>
              </Button>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-border bg-bg-body border-t px-6 py-6">
          <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-4 md:flex-row">
            <div className="text-bone-muted flex items-center gap-2 text-sm">
              <AppLogoIcon className="text-red h-5 w-5" />
              ToDoOrDie © {new Date().getFullYear()}
            </div>
            <div className="text-bone-muted flex gap-6 text-sm">
              <Link
                href="/privacy"
                className="hover:text-bone transition-colors"
              >
                Privacy
              </Link>
              <Link href="/terms" className="hover:text-bone transition-colors">
                Terms
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
